import React from 'react';
import { aboutContent } from '../data/mock';
import { Target, Eye } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const AboutSection = () => {
  const { isDark } = useTheme();

  return (
    <section 
      id="about" 
      aria-labelledby="about-heading"
      className={`py-16 sm:py-24 lg:py-32 ${isDark ? 'bg-[#111916]' : 'bg-white'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section header */}
        <header className="mb-12 sm:mb-16">
          <p className={`font-medium text-xs sm:text-sm uppercase tracking-[0.2em] mb-3 sm:mb-4 ${
            isDark ? 'text-green-500' : 'text-green-600'
          }`}>
            About Us
          </p>
          <h2 id="about-heading" className="font-display font-bold leading-tight mb-4 sm:mb-6" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>{aboutContent.title}</span>
          </h2>
        </header>

        {/* Main description */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 mb-12 sm:mb-20">
          <div>
            <p className={`text-lg md:text-xl leading-relaxed ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {aboutContent.description}
            </p>
          </div>
          <div className="flex items-center" aria-hidden="true">
            <div className={`w-full h-px ${
              isDark 
                ? 'bg-gradient-to-r from-green-800/50 via-amber-700/30 to-transparent' 
                : 'bg-gradient-to-r from-green-300 via-amber-200 to-transparent'
            }`} />
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Mission Card */}
          <article className={`premium-card rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10 border ${
            isDark 
              ? 'bg-[#1A2420] border-green-900/30' 
              : 'bg-gradient-to-br from-green-50/50 to-white border-green-100'
          }`}>
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#006633] to-[#00873D] flex items-center justify-center shadow-lg shadow-green-500/20">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" aria-hidden="true" />
              </div>
              <h3 className={`font-display font-semibold text-lg sm:text-xl ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {aboutContent.mission.title}
              </h3>
            </div>
            <p className={`text-sm sm:text-base leading-relaxed ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {aboutContent.mission.text}
            </p>
          </article>

          {/* Vision Card */}
          <article className={`premium-card rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10 border ${
            isDark 
              ? 'bg-[#1A2420] border-green-900/30' 
              : 'bg-gradient-to-br from-amber-50/50 to-white border-amber-100'
          }`}>
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#FF8C00] to-[#FFD700] flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white" aria-hidden="true" />
              </div>
              <h3 className={`font-display font-semibold text-lg sm:text-xl ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {aboutContent.vision.title}
              </h3>
            </div>
            <p className={`text-sm sm:text-base leading-relaxed ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {aboutContent.vision.text}
            </p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
